import Big from 'big.js'

import { Invoice } from 'Invoices/types'
import { sumAppliedCreditNotes } from 'Invoices/utils/sumAppliedCreditNotes'
import { sumCurrentPayments } from 'Invoices/utils/sumCurrentPayments'
import {
  currentCreditNotes,
  currentPayments
} from 'Invoices/utils/currentlyApplied'

/**
 * Money in the drawer is a major-unit decimal string (e.g. '1000.00'), the same
 * representation the invoice API, `toMoney`, and the invoice totals use. All
 * arithmetic runs through Big.js on those strings so decimals stay exact,
 * matching how invoice totals are summed elsewhere (see `sumCurrentPayments`
 * / `sumAppliedCreditNotes`). We never convert to integer minor units or float.
 */

/** Subtract one or more decimal-string amounts from a base, as a 2dp string. */
export const subtractAmounts = (base: string, ...amounts: string[]): string =>
  amounts.reduce((acc, amount) => acc.minus(amount), new Big(base)).toFixed(2)

/** Clamp a decimal-string amount to a floor of zero, as a 2dp string. */
export const clampToZero = (amount: string): string => {
  const value = new Big(amount)
  return (value.lt(0) ? new Big(0) : value).toFixed(2)
}

/** Negate a decimal-string amount, as a 2dp string (used for deduction rows). */
export const negateAmount = (amount: string): string =>
  new Big(amount).times(-1).toFixed(2)

export const isPositiveAmount = (amount: string): boolean =>
  new Big(amount).gt(0)

/**
 * Parse a free-text amount into a normalised 2dp decimal string, rounding
 * half-up (so '1.005' becomes '1.01', not '1.00'). Returns null when the input
 * is empty or not a valid number.
 */
export const parseAmount = (input: string): string | null => {
  const trimmed = input.trim()
  if (!trimmed) return null

  try {
    return new Big(trimmed).toFixed(2, Big.roundHalfUp)
  } catch {
    return null
  }
}

export const parseReference = (input: string): string | undefined =>
  input.trim() || undefined

/**
 * Serialise a picked payment date as UTC midnight of the selected calendar day.
 * The date picker yields local midnight, so `.toISOString()` would shift east-
 * of-UTC users to the previous day; building from the local Y/M/D avoids that.
 */
export const toPaymentDateIso = (date: Date): string =>
  new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  ).toISOString()

/**
 * Live feedback for the amount field. Only 'partial' and 'exact' allow submit;
 * everything else blocks it (see canSubmitPaymentAmount).
 */
export type PaymentAmountFeedback =
  | { type: 'empty' }
  | { type: 'nonPositive' }
  | { type: 'exceeds'; balance: string }
  | { type: 'partial'; remaining: string }
  | { type: 'exact' }

export const getPaymentAmountFeedback = (
  amount: string | null,
  balance: string
): PaymentAmountFeedback => {
  if (amount === null) return { type: 'empty' }

  const amountValue = new Big(amount)
  if (amountValue.lte(0)) return { type: 'nonPositive' }

  const balanceValue = new Big(balance)
  if (amountValue.gt(balanceValue)) return { type: 'exceeds', balance }
  if (amountValue.eq(balanceValue)) return { type: 'exact' }

  return {
    type: 'partial',
    remaining: balanceValue.minus(amountValue).toFixed(2)
  }
}

export const canSubmitPaymentAmount = (
  feedback: PaymentAmountFeedback
): boolean => feedback.type === 'partial' || feedback.type === 'exact'

export type InvoiceBalanceDetails = {
  /** Credit notes already applied to the invoice. */
  credited: string
  /** Payments already recorded against the invoice. */
  priorPaid: string
  /** Owed after credit notes but before any payments. */
  totalDue: string
  /** Outstanding balance owed, floored at zero and normalised to 2dp. */
  balance: string
}

/**
 * Settlement figures shared by the record-payment drawer and the mark-as-paid
 * confirm dialog. Prefers the ledger `balance` (already nets credits/payments);
 * falls back to total due less prior payments on older backends. clampToZero
 * normalises to 2dp so the seeded amount and the "exceeds" check agree.
 */
export const getInvoiceBalanceDetails = (
  invoice: Invoice
): InvoiceBalanceDetails => {
  const credited = sumAppliedCreditNotes(currentCreditNotes(invoice))
  const priorPaid = sumCurrentPayments(currentPayments(invoice))
  const totalDue = subtractAmounts(invoice.grossTotal, credited)
  const balance = clampToZero(
    invoice.balance ?? subtractAmounts(totalDue, priorPaid)
  )

  return { credited, priorPaid, totalDue, balance }
}
